import { pageTitle } from '@palaccount/config';
import { SEE_ITEM_COST, type QuotationResponse } from '@palaccount/contracts';
import { CheckCircle2, Copy, FileClock, Plus, Send, Trash2, X, XCircle } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router';

import { Amount, Ltr } from '@/components/amount';
import { DeleteConfirmBox } from '@/components/confirm-box';
import { useUnsavedChanges } from '@/components/document-header-controls';
import { DocumentToolbar } from '@/components/document-toolbar';
import { useLineImport } from '@/features/documents/line-tools';
import { DocumentTotals, TwoWayPair } from '@/features/documents/document-totals';
import { DescriptionLangToggle, useDescriptionLanguage } from '@/features/documents/description-language';
import { Gated } from '@/components/permission-gate';
import { QuoteStatusChip } from '@/components/status-chip';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Textarea } from '@/components/ui/textarea';
import { NativeSelect } from '@/components/ui/native-select';
import { AttachmentsPanel } from '@/features/attachments/attachments-panel';
import { DocumentActions } from '@/features/documents/document-actions';
import { useRateWarningGate } from '@/features/documents/fx-panel';
import { quotationDocument } from '@/features/documents/record-documents';
import { useSurfacePeople } from '@/features/hr/api';
import { PersonSourceHint } from '@/features/hr/person-source';
import { usePriceTiers } from '@/features/inventory/api';
import { ItemCell } from '@/features/inventory/item-cell';
import { useItemPicker } from '@/features/inventory/item-picker';
import { useWarehouses } from '@/features/inventory/stock-api';
import { PartnerBalanceChip } from '@/features/partners/partner-balance-chip';
import { usePartnerPicker } from '@/features/partners/partner-picker';
import { PartnerTypeahead } from '@/features/partners/partner-typeahead';
import { useOrgSetting } from '@/features/admin/api';
import { denyPermission, useCan } from '@/lib/permissions';
import { ApiError, localizeServerCause } from '@/lib/server-messages';
import { cn } from '@/lib/utils';
import { useBaseCurrency, useCompanyToday, useDocumentDate } from '@/lib/session';

import { discountGate } from './discount-gate';
import { BelowCostHint, LineStockHint, OverStockStrip, belowCost, shortBy } from './line-stock-warning';
import { QuoteAcceptanceCard } from './quote-acceptance';
import { allowedTransitions, canConvert, canDelete, canRevise, isEditable } from './quote-flow';
import { applicableTierId, tierColumns, tierPriceFor } from './quote-tiers';
import {
  useConvertToInvoice,
  useConvertToOrder,
  useCreateQuotation,
  useDeleteQuotation,
  useNewRevision,
  useQuotation,
  useSetQuotationStatus,
  useUpdateQuotation,
} from './quotes-api';
import {
  LineUnitSelect,
  SalesHeaderFields,
  salesHeaderCreateBody,
  salesHeaderFromDoc,
  salesHeaderPatchBody,
  useLineColumns,
  useSalesHeaderState,
} from './sales-header-fields';
import { useRateInput } from '@/features/documents/rate-field';
import { useDocCurrency } from '@/features/documents/doc-currency';
import { CollapsibleHeaderCard } from '@/features/documents/header-card';
import { useCommissionInput } from '@/features/sales/commission-field';
import { CopyFromModal, type CopiedDocument } from './copy-from-modal';
import { MarginPanel, netAmount } from './margin-panel';
import { computeTotalsMirror, type TwoWayState } from './totals-mirror';

interface GridLine {
  key: number;
  itemId: string;
  /** INV-L3 (QUO-01): the line's warehouse; '' = the header's default. */
  warehouseId: string;
  /** INV-L4: the unit the qty is typed in; '' = the item's stock unit. */
  unitId: string;
  description: string;
  qty: string;
  /** INV-L1: the free quantity beside qty; '' = none. */
  bonusQty: string;
  unitPrice: string;
  discountPct: string;
  /**
   * BR-MD-03-14's "already typed" — a fact about the CELL, set by its own
   * `onChange` and by nothing else. The reasoning, and the three cases that
   * rule out the two simpler tests ("is it empty" and "does it still hold
   * what the pick put there"), is written out once on the same field in
   * `sales/invoice-editor-page.tsx`.
   */
  typedDescription: boolean;
  typedUnitPrice: boolean;
}

let nextKey = 1;
const blankLine = (): GridLine => ({
  key: nextKey++,
  itemId: '',
  warehouseId: '',
  unitId: '',
  description: '',
  qty: '',
  bonusQty: '',
  unitPrice: '',
  discountPct: '',
  typedDescription: false,
  typedUnitPrice: false,
});

/** INV-L1: a stored bonus of 0.000 is an empty cell, not a zero to read. */
function bonusText(stored: string | undefined): string {
  // Tolerates a line written before the column existed (and older fixtures).
  if (stored === undefined || /^0+(\.0+)?$/.test(stored)) return '';
  return stored.includes('.') ? stored.replace(/\.?0+$/, '') : stored;
}

const MONEY_RE = /^\d{1,13}(\.\d{1,2})?$/;
const PCT_RE = /^\d{1,3}(\.\d{1,2})?$/;
const QTY_RE = /^\d{1,12}(\.\d{1,3})?$/;

function plusDays(iso: string, days: number): string {
  const date = new Date(`${iso}T00:00:00Z`);
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
}

function fromQuotation(quotation: QuotationResponse): GridLine[] {
  return quotation.lines.map((line) => ({
    key: nextKey++,
    itemId: line.itemId ?? '',
    warehouseId: line.warehouseId ?? '',
    unitId: line.unitId ?? '',
    description: line.description,
    qty: line.qty,
    bonusQty: bonusText(line.bonusQty),
    unitPrice: line.unitPrice,
    discountPct: line.discountPct,
    // A saved line's text and price are somebody's typed work.
    typedDescription: true,
    typedUnitPrice: true,
  }));
}

function CellInput({ mono, className, ...props }: React.ComponentProps<'input'> & { mono?: boolean }) {
  return (
    <input
      className={cn(
        'h-9 w-full min-w-0 rounded-md border border-transparent bg-transparent px-2 text-sm outline-none transition-colors',
        'hover:border-input focus-visible:border-ring focus-visible:ring-2 focus-visible:ring-ring/50',
        'disabled:cursor-not-allowed disabled:opacity-70',
        mono && 'text-end font-mono tabular-nums',
        className,
      )}
      {...props}
    />
  );
}

/**
 * SAL-01 — the Quotation editor: header with the live Valid-until field
 * (BR-SAL-01-04), the item-linked grid (MD-03 picker with a free-text
 * fallback), the SAL-00 totals pair, the guarded status ladder, NEW REVISION
 * with the frozen history table (§9, BR-SAL-01-10), and the two conversions
 * that expiry never blocks (R4, BR-SAL-01-09). Posts nothing, ever
 * (BR-SAL-01-11) — there is no Post button on this screen by design.
 */
export function QuoteEditorPage() {
  // N-16: the COMPANY's today, never the reader's machine clock.
  const today = useCompanyToday();
  const { t, i18n } = useTranslation();
  // The reader's language is the PDF's; `print.language` may override it in
  // the template (INV-H6), for the worker and the browser alike.
  const printLocale = i18n.language.startsWith('ar') ? ('ar' as const) : ('en' as const);
  const navigate = useNavigate();
  const { quotationId } = useParams<{ quotationId: string }>();
  const isNew = !quotationId;

  const { data: quotation, isPending: loading } = useQuotation(quotationId);
  const createQuotation = useCreateQuotation();
  const updateQuotation = useUpdateQuotation(quotationId ?? '');
  const deleteQuotation = useDeleteQuotation(quotationId ?? '');
  const setQuotationStatus = useSetQuotationStatus(quotationId ?? '');
  const newRevision = useNewRevision(quotationId ?? '');
  const convertToInvoice = useConvertToInvoice(quotationId ?? '');
  const convertToOrder = useConvertToOrder(quotationId ?? '');

  const canEdit = useCan(isNew ? 'sales.create' : 'sales.edit');
  /** BR-SAL-01-16: the revision action is gated on ONE permission only. */

  const [partnerId, setPartnerId] = useState('');
  /** Field 12 — Sales rep (BR-SAL-01-15): '' = not recorded. */
  const [salesRepId, setSalesRepId] = useState('');
  const [quotationDate, setQuotationDate] = useDocumentDate();
  const [validUntil, setValidUntil] = useState(plusDays(today, 14));
  const [reference, setReference] = useState('');
  const [terms, setTerms] = useState('');
  /** INV-F1…F6 (QUO-01) — the shared sales header; a new document's salesperson is me. */
  const [header, setHeader] = useSalesHeaderState(isNew);
  // Wave 4D — the editable rate (features/documents/rate-field).
  const rate = useRateInput(quotation);
  // Wave 4D — the commission % (features/sales/commission-field).
  const commission = useCommissionInput(quotation);
  const { bonusOn, discountOn } = useLineColumns();
  const { data: warehouses } = useWarehouses();
  const [lines, setLines] = useState<GridLine[]>([blankLine()]);
  const [discount, setDiscount] = useState<TwoWayState | undefined>(undefined);
  const [tax, setTax] = useState<TwoWayState | undefined>(undefined);
  const [notes, setNotes] = useState('');
  /** Wave 4B: whom the offer is addressed to ("Attn:"), printed on the PDF. */
  const [attention, setAttention] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [deleting, setDeleting] = useState(false);
  /** Wave 4B — Copy from… on a NEW quotation (INV-H7's modal, sales sources only). */
  const [copyOpen, setCopyOpen] = useState(false);
  const [copyNote, setCopyNote] = useState<string | null>(null);
  /** BR-MD-11-14's question, in ADM-02 §8's shared box (BR-PERM-15). */
  const { confirmRateWarning, rateWarningDialog } = useRateWarningGate();
  const loadedId = useRef<string | null>(null);
  /** S4 — the header rail's ‹‹ ›› wait while there is typed work (see `useUnsavedChanges`). */
  const dirty = useUnsavedChanges(
    [partnerId, salesRepId, quotationDate, validUntil, reference, terms, attention, header, lines, discount, tax, notes],
    isNew ? 'new' : quotation && loadedId.current === quotation.id ? `${quotation.id}:${quotation.revisionNo}` : null,
  );
  const loadedRevision = useRef<number>(0);

  const status = quotation?.status ?? 'draft';
  const revisionNo = quotation?.revisionNo ?? 1;
  const editable = isEditable(status) && canEdit;

  useEffect(() => {
    document.title = pageTitle(t('quotes.editorTitle'));
  }, [t]);

  // Adopt the server's stored document wholesale — and re-adopt after a NEW
  // REVISION reopens it (the revision number is part of the identity).
  useEffect(() => {
    if (quotation && (loadedId.current !== quotation.id || loadedRevision.current !== quotation.revisionNo)) {
      loadedId.current = quotation.id;
      loadedRevision.current = quotation.revisionNo;
      setPartnerId(quotation.partnerId);
      setSalesRepId(quotation.salesRepEmployeeId ?? '');
      setQuotationDate(quotation.quotationDate);
      setValidUntil(quotation.validUntil);
      setReference(quotation.reference ?? '');
      setTerms(quotation.terms ?? '');
      setAttention(quotation.attention ?? '');
      setHeader(salesHeaderFromDoc(quotation));
      setLines(fromQuotation(quotation));
      setDiscount({ basis: 'pct', value: quotation.discountPct });
      setTax({ basis: 'pct', value: quotation.taxPct });
      setNotes(quotation.notes ?? '');
    }
  }, [quotation]);

  // GEN-50 — one panel of matches, plus the records these lines already name.
  const customerPicker = usePartnerPicker(partnerId || null, { type: 'customer' });
  const itemPicker = useItemPicker(
    useMemo(() => lines.map((line) => line.itemId), [lines]),
    { status: 'active' },
  );

  const partner = customerPicker.byId.get(partnerId);
  // GEN-108's chain, ending where it should: the DOCUMENT's own code, else
  // the party's usual currency, else the company's base (GEN-107) read from
  // the session — never a literal, which printed dollars on a euro
  // company's every new document until a party was picked.
  const base = useBaseCurrency();
  // Per-document currency (2026-10-30): the header's dropdown in front of the chain.
  const docCurrency = useDocCurrency({
    partnerId,
    savedCurrency: quotation?.currency,
    partnerCurrency: partner?.currency ?? base,
    onPick: () => rate.setText(''),
  });
  const currency = docCurrency.currency;
  const currencyLocked = !editable || status !== 'draft';

  // EC-19-73: the tax box opens at the COMPANY default VAT rate.
  const defaultTaxPct = useOrgSetting<string>('invoicing.default_vat_pct');
  const totals = useMemo(
    () => computeTotalsMirror(lines, discount, tax, currency, defaultTaxPct),
    [lines, discount, tax, currency, defaultTaxPct],
  );

  /**
   * SAL-01's discount-approval pair. The threshold is the settings hub's
   * `invoicing.max_discount_pct` (§16 question 2's proposed default), the
   * percentage is the effective total discount, lines included
   * (BR-SAL-01-08), and the permission decides the colour: amber for a user
   * who cannot approve it (ERR-SAL-01-03), green for one who can
   * (ERR-SAL-01-04 / BR-SAL-01-07). The server refuses the SEND either way
   * (BR-SAL-01-06) — this strip is the warning in front of it.
   */
  const discountThreshold = useOrgSetting<string>('invoicing.max_discount_pct');
  const holdsApprove = useCan('sales.approve');
  /** SAL-01 §5a switch 3: adds the whole customer-acceptance card. */
  const signatureOn = useOrgSetting<boolean>('invoicing.quote_customer_signature');

  /**
   * SAL-01 §5a switch 1 (owner ruling R5): the single Unit-price column
   * becomes one column per tier the COMPANY has defined, in the company's
   * own order (BR-SAL-01-05a — never a fixed three), and the line total
   * uses the tier that applies to THIS customer (BR-SAL-01-05): the
   * applicable tier's column is the editable line price, seeded from the
   * item's stored price for that band; the other columns are the item's
   * stored prices, read-only. A tenant with the switch on but no tiers —
   * or a reader whose role cannot list them (the tiers API is
   * inventory.view, same as the item search this grid already leans on) —
   * keeps the single-column grid.
   */
  const multiTierOn = useOrgSetting<boolean>('invoicing.quote_multi_tier_pricing');
  const tiersQuery = usePriceTiers(false, { enabled: multiTierOn });
  const tierCols = useMemo(
    () => (multiTierOn ? tierColumns(tiersQuery.data ?? []) : []),
    [multiTierOn, tiersQuery.data],
  );
  const applicableTier = useMemo(
    () => applicableTierId(partner?.priceTierId, tierCols),
    [partner, tierCols],
  );
  /** How many price columns the grid is drawing (the footer's colSpan math). */
  const priceColumnCount = tierCols.length > 0 ? tierCols.length : 1;

  /** Field 12's feed (BR-SAL-01-15 via BR-HR-01-05d's sales_rep surface). */
  const salesRepPeople = useSurfacePeople('sales_rep');
  const discountBanner = useMemo(
    () => (isEditable(status) ? discountGate(lines, totals, currency, discountThreshold, holdsApprove) : null),
    [status, lines, totals, currency, discountThreshold, holdsApprove],
  );

  const setLine = (key: number, patch: Partial<GridLine>) => {
    setLines((current) => current.map((line) => (line.key === key ? { ...line, ...patch } : line)));
  };

  /**
   * INV-L6 / INV-L7 — the invoice grid's two warnings, on the quotation too
   * (see `line-stock-warning.tsx`). A quotation names no warehouse, so the
   * stock compared is the item's TOTAL available; cost only for a reader
   * allowed to see it. Warn, never block — a quote reserves nothing.
   */
  const lineShort = (line: GridLine): string | null => {
    if (!editable || line.unitId !== '') return null;
    const warehouseId = line.warehouseId || header.warehouseId;
    return shortBy(
      itemPicker.byId.get(line.itemId),
      line.qty,
      warehouseId ? { warehouseId } : { total: true },
    );
  };
  const anyShort = lines.some((line) => lineShort(line) !== null);
  const canSeeCost = useCan(SEE_ITEM_COST);
  const lineBelowCost = (line: GridLine): string | null =>
    editable && canSeeCost
      ? belowCost(line.unitPrice, itemPicker.byId.get(line.itemId)?.costPrice)
      : null;

  /**
   * BR-MD-03-14: picking fills the description and price; both stay editable,
   * and it never overwrites typed work.
   */
  const pickItem = (key: number, id: string) => {
    const item = itemPicker.byId.get(id);
    if (!item) {
      setLine(key, { itemId: '' });
      return;
    }
    setLines((current) =>
      current.map((line) =>
        line.key === key
          ? {
              ...line,
              itemId: id,
              // INV-L4: the picked unit belonged to the OLD item.
              unitId: '',
              // Per FIELD, not per line: a typed description must not stop
              // the untouched price filling.
              description: line.typedDescription ? line.description : descLang.describe(item),
              // BR-SAL-01-05: with multi-tier on, the price that fills in is
              // the tier that applies to THIS customer — the item's stored
              // band price, unit price as the fallback. Off, the unit price
              // as ever.
              unitPrice: line.typedUnitPrice
                ? line.unitPrice
                : tierCols.length > 0
                  ? tierPriceFor(item, applicableTier)
                  : item.unitPrice,
            }
          : line,
      ),
    );
  };

  const removeLine = (key: number) => {
    setLines((current) => {
      const remaining = current.filter((line) => line.key !== key);
      return remaining.length === 0 ? [blankLine()] : remaining;
    });
  };

  const usedLines = lines.filter(
    (line) => line.description.trim() !== '' || line.qty.trim() !== '' || line.unitPrice.trim() !== '',
  );

  const validate = (): string | null => {
    if (!partnerId) return t('sales.errNoCustomer');
    if (validUntil < quotationDate) return t('quotes.errValidUntil');
    // `quotes.errNoLines`, not `sales.errNoLines`: a quotation is CONVERTED to
    // an order or an invoice, and is never posted. The server's own no-lines
    // refusal for this document is `QUOTATION_MESSAGES.noLines` — "Add at least
    // one item before converting this quotation." — and this line is the
    // browser's mirror of that same gate, so it has to say the same thing. It
    // read `sales.errNoLines` ("…before posting.") and the Arabic value of that
    // key ends "قبل الترحيل" — posting — so an Arabic reader was told to post a
    // document this product never posts. Same shape as the PUR-01 send door,
    // pinned in `po-send-door-arabic.test.tsx`; this one is pinned in
    // `quote-convert-door-arabic.test.tsx`.
    if (usedLines.length === 0) return t('quotes.errNoLines');
    for (const [index, line] of usedLines.entries()) {
      if (line.description.trim() === '') return t('sales.errLineDescription', { line: index + 1 });
      if (!QTY_RE.test(line.qty.trim()) || /^0+(\.0+)?$/.test(line.qty.trim()))
        return t('sales.errLineQty', { line: index + 1 });
      if (!MONEY_RE.test(line.unitPrice.trim())) return t('sales.errLinePrice', { line: index + 1 });
      const bonus = line.bonusQty.trim();
      if (bonusOn && bonus !== '' && !QTY_RE.test(bonus)) return t('sales.errLineBonus', { line: index + 1 });
      const pct = line.discountPct.trim();
      if (discountOn && pct !== '' && !PCT_RE.test(pct)) return t('sales.errDiscountRange');
    }
    if (discount && !(discount.basis === 'pct' ? PCT_RE : MONEY_RE).test(discount.value.trim()))
      return t('sales.errDiscountRange');
    if (tax && !(tax.basis === 'pct' ? PCT_RE : MONEY_RE).test(tax.value.trim()))
      return t('sales.errTaxRange');
    return null;
  };

  const buildTwoWay = (state: TwoWayState | undefined) =>
    state === undefined
      ? undefined
      : state.basis === 'pct'
        ? { pct: state.value.trim() }
        : { amount: state.value.trim() };

  // The invoice grid's line shape, on the quotation (QUO-01): INV-L3's
  // header-default warehouse, INV-L4's unit, INV-L1's bonus, INV-L2's gate.
  const requestLines = () =>
    usedLines.map((line) => {
      const warehouseId = line.warehouseId || header.warehouseId;
      return {
        ...(line.itemId ? { itemId: line.itemId } : {}),
        ...(line.itemId && warehouseId ? { warehouseId } : {}),
        ...(line.itemId && line.unitId ? { unitId: line.unitId } : {}),
        description: line.description.trim(),
        qty: line.qty.trim(),
        ...(bonusOn && line.bonusQty.trim() !== '' ? { bonusQty: line.bonusQty.trim() } : {}),
        unitPrice: line.unitPrice.trim(),
        ...(discountOn && line.discountPct.trim() !== ''
          ? { discountPct: line.discountPct.trim() }
          : {}),
      };
    });

  const handleFailure = (
    thrown: unknown,
    permission: 'sales.create' | 'sales.edit' | 'sales.delete',
  ) => {
    if (thrown instanceof ApiError && thrown.status === 403) denyPermission(permission);
    // The line above already reads the cause to spot a 403; this one used to
    // throw it away one expression later. `localizeServerCause` keeps it: a
    // refusal keeps the server's sentence localized (`QUOTATION_MESSAGES` are
    // mapped in STATIC_MESSAGE_KEYS), a request that never answered gets this
    // screen's own sentence rather than the browser's `Failed to fetch`.
    setError(localizeServerCause(t, thrown, 'quotes.errSaveFailed'));
  };

  /**
   * Wave 4B — Copy from… (INV-H7's pure client-side copy): the lines land as
   * typed work after whatever is already typed, and the customer is taken
   * only while the header is still empty. Nothing is linked.
   */
  const onCopyFrom = (copiedDoc: CopiedDocument) => {
    setLines((current) => {
      const kept = current.filter(
        (line) => line.description.trim() !== '' || line.qty.trim() !== '' || line.unitPrice.trim() !== '',
      );
      const added: GridLine[] = copiedDoc.lines.map((line) => ({
        ...blankLine(),
        ...line,
        typedDescription: true,
        typedUnitPrice: true,
      }));
      return [...kept, ...added];
    });
    if (!partnerId && copiedDoc.partnerId) setPartnerId(copiedDoc.partnerId);
    setCopyNote(t('sales.copyFromDone', { lines: copiedDoc.lines.length }));
  };

  /** Wave 4C — lines from a small .xlsx/.csv, resolved by item code; unknown codes stay free text. */
  /** Wave 4C — which name a picked/imported item writes into a NEW line description. */
  const descLang = useDescriptionLanguage();
  const lineImport = useLineImport({
    id: 'quote',
    side: 'sales',
    describe: descLang.describe,
    copyLines: () =>
      lines
        .filter((line) => line.itemId !== '' || line.description.trim() !== '')
        .map((line) => ({
          code: itemPicker.byId.get(line.itemId)?.code ?? '',
          description: line.description,
          qty: line.qty,
          unitPrice: line.unitPrice,
          discountPct: line.discountPct,
        })),
    visible: isEditable(status),
    gate: {
      permission: isNew ? 'sales.create' : 'sales.edit',
      reasonKey: isNew ? 'perm.cannot.createQuotes' : 'perm.cannot.editQuotes',
    },
    disabled: busy || !editable,
    onLines: (imported) =>
      setLines((current) => [
        ...current.filter(
        (line) =>
          line.itemId !== '' ||
          line.description.trim() !== '' ||
          line.qty.trim() !== '' ||
          line.unitPrice.trim() !== '',
      ),
        ...imported.map((line) => ({
          ...blankLine(),
          itemId: line.itemId,
          description: line.description,
          qty: line.qty,
          unitPrice: line.unitPrice,
          discountPct: line.discountPct,
          typedDescription: true,
          typedUnitPrice: true,
        })),
      ]),
  });

  const onSave = async () => {
    setError(null);
    const problem = validate();
    if (problem) {
      setError(problem);
      return;
    }
    setBusy(true);
    try {
      if (isNew) {
        const created = await createQuotation.mutateAsync({
          partnerId,
          quotationDate,
          validUntil,
          ...(salesRepId ? { salesRepEmployeeId: salesRepId } : {}),
          ...salesHeaderCreateBody(header),
          ...docCurrency.body(),
          ...rate.createBody(),
          ...commission.createBody(),
          ...(reference.trim() ? { reference: reference.trim() } : {}),
          ...(terms.trim() ? { terms } : {}),
          ...(attention.trim() ? { attention: attention.trim() } : {}),
          lines: requestLines(),
          ...(discount ? { discount: buildTwoWay(discount)! } : {}),
          ...(tax ? { tax: buildTwoWay(tax)! } : {}),
          ...(notes.trim() ? { notes } : {}),
        });
        void navigate(`/sales/quotes/${created.id}`, { replace: true });
      } else {
        await updateQuotation.mutateAsync({
          quotationDate,
          validUntil,
          salesRepEmployeeId: salesRepId === '' ? null : salesRepId,
          ...salesHeaderPatchBody(header),
          ...docCurrency.body(),
          ...rate.patchBody(),
          ...commission.patchBody(),
          reference: reference.trim() === '' ? null : reference.trim(),
          terms: terms.trim() === '' ? null : terms,
          attention: attention.trim() === '' ? null : attention.trim(),
          lines: requestLines(),
          ...(discount ? { discount: buildTwoWay(discount)! } : {}),
          ...(tax ? { tax: buildTwoWay(tax)! } : {}),
          notes: notes.trim() === '' ? null : notes,
        });
        loadedId.current = null;
      }
    } catch (thrown) {
      handleFailure(thrown, isNew ? 'sales.create' : 'sales.edit');
    } finally {
      setBusy(false);
    }
  };

  const onDelete = async () => {
    setDeleting(false);
    if (!quotationId) return;
    setBusy(true);
    setError(null);
    try {
      await deleteQuotation.mutateAsync();
      void navigate('/sales/quotes');
    } catch (thrown) {
      handleFailure(thrown, 'sales.delete');
    } finally {
      setBusy(false);
    }
  };

  const onTransition = async (target: 'sent' | 'accepted' | 'rejected', signature?: string) => {
    // BR-MD-11-14 asks its question at the CONFIRMING act, and for a quotation
    // that act is draft → sent: the moment the priced document leaves the
    // building, and the only transition that freezes a rate. Accepting or
    // rejecting a quotation a fortnight later re-reads nothing, so there is
    // nothing to warn about there.
    // The `&&` still short-circuits, so accept and reject do not await
    // anything at all — they never reach the call, exactly as before. The one
    // transition that freezes a rate is the one that asks.
    if (target === 'sent' && !(await confirmRateWarning(quotation?.rateWarning))) return;
    setBusy(true);
    setError(null);
    try {
      // BR-SAL-01-13: the acceptance card's drawing rides the acceptance act
      // so the server can store it with the quotation.
      await setQuotationStatus.mutateAsync({
        status: target,
        ...(signature !== undefined ? { signature } : {}),
      });
      loadedId.current = null;
    } catch (thrown) {
      handleFailure(thrown, 'sales.edit');
    } finally {
      setBusy(false);
    }
  };

  /** §9: freeze the prior version, reopen as Draft (BR-SAL-01-10). */
  const onNewRevision = async () => {
    setBusy(true);
    setError(null);
    try {
      await newRevision.mutateAsync();
    } catch (thrown) {
      handleFailure(thrown, 'sales.edit');
    } finally {
      setBusy(false);
    }
  };

  const onConvert = async (target: 'invoice' | 'order') => {
    setBusy(true);
    setError(null);
    try {
      if (target === 'invoice') {
        const result = await convertToInvoice.mutateAsync();
        void navigate(`/sales/${result.invoiceId}`);
      } else {
        const result = await convertToOrder.mutateAsync();
        void navigate(`/sales/orders/${result.orderId}/edit`);
      }
    } catch (thrown) {
      handleFailure(thrown, 'sales.create');
    } finally {
      setBusy(false);
    }
  };

  if (!isNew && loading) {
    return <div className="h-64 animate-pulse rounded-xl bg-muted" aria-busy />;
  }

  const transitions = allowedTransitions(status);
  // With the customer-signature switch ON, accepting belongs to the
  // acceptance card's **Accept quote** (SAL-01 §5 A5) — the plain workflow
  // button would only earn the server's ERR-SAL-01-07 refusal, so it is not
  // offered. Off, acceptance stays a plain status act as before.
  const workflowTransitions = signatureOn
    ? transitions.filter((target) => target !== 'accepted')
    : transitions;
  const transitionMeta = {
    sent: { key: 'quotes.actSend', icon: Send },
    accepted: { key: 'quotes.actAccept', icon: CheckCircle2 },
    rejected: { key: 'quotes.actReject', icon: XCircle },
  } as const;

  return (
    <div className="grid gap-4">
      {/* BR-MD-11-14's question in ADM-02 §8's shared box. Rendered
          UNCONDITIONALLY, unlike the delete box below: this one is awaited, so
          a dialog that unmounted mid-question would leave the transition
          hanging with no error anywhere. See `useRateWarningGate`. */}
      {rateWarningDialog}
      {/* ── Toolbar: identity, revision chip, lifecycle, actions ─────────── */}
      <div className="sticky top-0 z-10 flex flex-wrap items-center gap-3 bg-background">
        <h2 className="text-lg font-bold">{t('quotes.editorTitle')}</h2>
        <span className="rounded-md bg-muted px-2 py-1 font-mono text-sm text-muted-foreground">
          {quotation ? <bdi dir="ltr">{quotation.code}</bdi> : t('quotes.codeNew')}
        </span>
        {/* §9 — "v2 (Current)": the working revision, always shown. */}
        <span className="rounded-full bg-violet-100 px-2.5 py-0.5 font-mono text-xs font-semibold text-violet-800 dark:bg-violet-950 dark:text-violet-300">
          <bdi dir="ltr">{t('quotes.revChip', { rev: revisionNo })}</bdi>
        </span>
        <QuoteStatusChip status={status} />
        {/* BR-SAL-01-09's amber warning rides the stored status. */}
        {status === 'expired' ? (
          <span className="text-xs font-medium text-amber-700 dark:text-amber-400">
            {t('quotes.expiredWarning')}
          </span>
        ) : null}
        {/* The toolbar's acts, DECLARED — see `document-toolbar.tsx`. */}
        <DocumentToolbar
          header={{ kind: 'quotation', id: quotationId, dirty }}
          actions={[
            {
              // Not PART 4.6's Cancel row: a link back to the list wearing the
              // same word. The primitive's header records the collision.
              id: 'back',
              row: null,
              label: t('common.cancel'),
              variant: 'outline',
              to: '/sales/quotes',
            },
            {
              // Wave 2 (specs/erp-gap-analysis.md) — the invoice's Print/PDF
              // pair, on the quotation. Same `DocumentActions`, same template
              // package, same DRAFT stamp from the template; the `node:`
              // escape the roster test pins by name. Saved documents only: a
              // new draft has no row for the worker to read.
              id: 'printPdf',
              row: 'print',
              node:
                !isNew && quotation ? (
                  <DocumentActions
                    request={{ kind: 'quotation', locale: printLocale, subjectId: quotation.id }}
                    payload={{ kind: 'quotation', data: quotationDocument(quotation) }}
                  />
                ) : null,
            },
            {
              // Wave 4B — INV-H7's Copy from…, on a NEW quotation: a pure
              // copy of another sales document's lines (no purchase bill —
              // an offer is priced to the customer, not from a supplier).
              id: 'copyFrom',
              row: null,
              label: t('sales.copyFrom'),
              icon: <Copy aria-hidden />,
              variant: 'outline',
              visible: isNew,
              gate: { permission: 'sales.create', reasonKey: 'perm.cannot.createQuotes' },
              onAct: () => setCopyOpen(true),
            },
            // Wave 4C — Import lines / Download template (shared line tools).
            ...lineImport.acts,
            {
              // `visible` here is the FLOW's function, not a permission: a
              // revised or converted quotation has no Delete for anybody. The
              // permission is the second question and it greys (BR-PERM-18),
              // which is exactly the split `visible` and `gate` exist to keep.
              // `canDelete` stays a CALL rather than being flattened to a
              // status test — a revised quote is undeletable at every status.
              id: 'delete',
              row: 'delete',
              label: t('common.delete'),
              icon: <Trash2 aria-hidden />,
              variant: 'destructive',
              visible: !isNew && canDelete(status, revisionNo),
              gate: { permission: 'sales.delete', reasonKey: 'perm.cannot.deleteQuotes' },
              disabled: busy,
              onAct: () => setDeleting(true),
            },
            {
              id: 'save',
              row: 'save',
              label: t('common.save'),
              visible: isEditable(status),
              gate: {
                permission: isNew ? 'sales.create' : 'sales.edit',
                reasonKey: isNew ? 'perm.cannot.createQuotes' : 'perm.cannot.editQuotes',
              },
              busy,
              onAct: () => void onSave(),
            },
          ]}
        >
          {/* GEN-58 asks its sentence in the product’s ONE shared box
              (GEN-204, GEN-209 — "deleting a draft" is first on its list).
              `window.confirm` was neither of BR-PERM-15’s two patterns.
              Inside the same guard as the button, so the dialog cannot
              outlive the condition that allows the delete. */}
          {!isNew && canDelete(status, revisionNo) ? (
            <DeleteConfirmBox
              open={deleting}
              detail={t('quotes.confirmDelete')}
              onCancel={() => setDeleting(false)}
              onConfirm={() => void onDelete()}
              busy={busy}
            />
          ) : null}
          {/* INV-H7's picker, inside the same guard as its button. */}
          {isNew && copyOpen ? (
            <CopyFromModal
              onClose={() => setCopyOpen(false)}
              onPick={onCopyFrom}
              sources={['quotation', 'salesOrder', 'invoice']}
              help={t('sales.copyFromHelpQuote')}
            />
          ) : null}
        </DocumentToolbar>
      </div>
      {copyNote ? (
        <p role="status" className="text-sm text-muted-foreground">
          {copyNote}
        </p>
      ) : null}

      {/* BR-SAL-01-10a as the locked notice on a sent+ quotation. */}
      {!isNew && !isEditable(status) && status !== 'converted' ? (
        <p className="rounded-md border border-blue-300 bg-blue-50 px-4 py-3 text-sm text-blue-900 dark:border-blue-800 dark:bg-blue-950 dark:text-blue-200">
          {t('quotes.lockedNotice')}
        </p>
      ) : null}
      {status === 'converted' ? (
        <p className="rounded-md border border-emerald-300 bg-emerald-50 px-4 py-3 text-sm text-emerald-900 dark:border-emerald-800 dark:bg-emerald-950 dark:text-emerald-200">
          {t('quotes.convertedNotice')}{' '}
          {quotation?.convertedInvoiceId ? (
            <Link className="font-semibold underline" to={`/sales/${quotation.convertedInvoiceId}`}>
              <bdi dir="ltr">{quotation.convertedInvoiceCode}</bdi>
            </Link>
          ) : quotation?.convertedOrderId ? (
            <Link
              className="font-semibold underline"
              to={`/sales/orders/${quotation.convertedOrderId}/edit`}
            >
              <bdi dir="ltr">{quotation.convertedOrderCode}</bdi>
            </Link>
          ) : null}
        </p>
      ) : null}
      {/* Wave 4C — the line import's chooser, summary and per-row problems. */}
      {lineImport.panel}
      {error ? (
        <p
          role="alert"
          className="rounded-md border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive"
        >
          {error}
        </p>
      ) : null}
      <OverStockStrip show={anyShort} />
      {/* The discount-approval pair, live over the edited figures. Amber is
          ERR-SAL-01-03's sentence — a warning ahead of the send refusal, so
          GEN-155's strip; green is ERR-SAL-01-04's confirmation for a
          `sales.approve` holder (BR-SAL-01-07). */}
      {discountBanner !== null ? (
        <p
          role="status"
          className={cn(
            'rounded-md border px-4 py-3 text-sm font-medium',
            discountBanner.kind === 'amber'
              ? 'border-amber-500/40 bg-amber-500/10 text-amber-900 dark:text-amber-200'
              : 'border-emerald-300 bg-emerald-50 text-emerald-900 dark:border-emerald-800 dark:bg-emerald-950 dark:text-emerald-200',
          )}
        >
          {discountBanner.kind === 'amber'
            ? t('quotes.discountOverThreshold', {
                pct: discountBanner.pct,
                threshold: discountBanner.threshold,
              })
            : t('quotes.discountWithinAuthority', { pct: discountBanner.pct })}
        </p>
      ) : null}

      {/* ── Header fields (SAL-01 §5b) ───────────────────────────────────── */}
      <CollapsibleHeaderCard
        storageKey="quotation"
        className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5"
        summary={{
          partner: quotation?.partnerName ?? partner?.name,
          date: quotationDate,
          currency,
          code: quotation?.code,
          status: <QuoteStatusChip status={status} />,
        }}
      >
          <div className="grid gap-1.5">
            <Label htmlFor="qt-date">{t('quotes.fDateIssued')}</Label>
            <Input
              id="qt-date"
              type="date"
              dir="ltr"
              value={quotationDate}
              onChange={(event) => setQuotationDate(event.target.value)}
              disabled={!editable}
            />
          </div>
          <div className="grid gap-1.5">
            {/* The LIVE field feeding the automatic expiry rule (§7). */}
            <Label htmlFor="qt-valid">{t('quotes.fValidUntil')}</Label>
            <Input
              id="qt-valid"
              type="date"
              dir="ltr"
              value={validUntil}
              onChange={(event) => setValidUntil(event.target.value)}
              disabled={!editable}
            />
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="qt-customer">{t('sales.fCustomer')}</Label>
            {/* GEN-50, and BR-MD-01-07: OFFERS the active, DISPLAYS whoever
                this quotation already names. */}
            <PartnerTypeahead
              entity="customer"
              id="qt-customer"
              label={t('sales.fCustomer')}
              value={partnerId || null}
              onChange={(id) => setPartnerId(id ?? '')}
              options={customerPicker.options}
              selected={customerPicker.optionFor(partnerId)}
              query={customerPicker.query}
              onQueryChange={customerPicker.setQuery}
              placeholder={t('sales.pickCustomer')}
              emptyLabel={t('picker.noMatchingCustomer')}
              loading={customerPicker.loading}
              disabled={!editable || !isNew}
            />
            {/* Wave 4B — the customer's balance and credit bar under the
                customer: the server's own figures, drawn and never decided here. */}
            <PartnerBalanceChip partner={partner} className="justify-self-start" />
          </div>
          <div className="grid gap-1.5">
            <Label>{t('sales.fCustomerId')}</Label>
            <div className="flex h-12 items-center gap-3 rounded-[11px] border border-input bg-muted/40 px-3 font-mono text-sm">
              <Ltr value={quotation?.partnerCode ?? partner?.code ?? '—'} />
              <span className="ms-auto text-muted-foreground">
                <Ltr value={currency} />
              </span>
            </div>
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="qt-ref">{t('quotes.fReference')}</Label>
            <Input
              id="qt-ref"
              value={reference}
              maxLength={50}
              onChange={(event) => setReference(event.target.value)}
              disabled={!editable}
            />
          </div>
          {/* Wave 4B — the attention line: the person this offer is addressed
              to, printed under the customer on the PDF. */}
          <div className="grid gap-1.5">
            <Label htmlFor="qt-attention">{t('quotes.fAttention')}</Label>
            <Input
              id="qt-attention"
              value={attention}
              maxLength={120}
              placeholder={t('quotes.attentionPh')}
              onChange={(event) => setAttention(event.target.value)}
              disabled={!editable}
            />
          </div>
          <div className="grid gap-1.5">
            {/* Field 12 — BR-SAL-01-15: the picker offers the people who
                actually sell — BR-HR-01-05d's sales_rep surface feed, never
                the prototype's user list (a CFO, a Financial Controller, no
                Emma Ward). A reader whose role cannot list people (the feed
                is hr.view) keeps a read-only field with the stored name. */}
            <Label htmlFor="qt-salesrep">{t('quotes.fSalesRep')}</Label>
            {salesRepPeople.data !== undefined ? (
              <NativeSelect
                id="qt-salesrep"
                value={salesRepId}
                onChange={(event) => setSalesRepId(event.target.value)}
                disabled={!editable}
              >
                <option value="">—</option>
                {salesRepPeople.data.map((person) => (
                  <option key={person.employeeId} value={person.employeeId}>
                    {person.fullName}
                  </option>
                ))}
                {/* A stored rep who has since left the feed still DISPLAYS
                    (the picker-offer rule governs new picks, not history). */}
                {salesRepId !== '' &&
                !salesRepPeople.data.some((person) => person.employeeId === salesRepId) ? (
                  <option value={salesRepId}>{quotation?.salesRepName ?? salesRepId}</option>
                ) : null}
              </NativeSelect>
            ) : (
              <div className="flex h-12 items-center rounded-[11px] border border-input bg-muted/40 px-3 text-sm">
                {quotation?.salesRepName ?? '—'}
              </div>
            )}
            {/* BR-HR-01-16: says out loud what the comment above establishes —
                EMPLOYEES through the sales_rep surface, never user accounts. */}
            <PersonSourceHint source="employees" surface="sales_rep" />
            {/* Wave 4B — ONE rep model, two fields, said out loud: this is the
                EMPLOYEE paid the commission; the shared header's Salesperson
                is the USER who owns the document. A new quotation with no rep
                picked takes the salesperson's own employee record when
                `employees.user_id` links one that holds the surface (server). */}
            <p className="text-xs text-muted-foreground">{t('quotes.salesRepHint')}</p>
          </div>
          {/* INV-F1…F6 + INV-F5 (QUO-01): the invoice's header, the same
              component, the FxPanel in the grid rather than under the totals. */}
          <SalesHeaderFields
            idPrefix="qt"
            partnerId={partnerId}
            state={header}
            onChange={(patch) => setHeader((current) => ({ ...current, ...patch }))}
            disabled={!editable}
            doc={quotation}
            rate={rate}
            commission={commission}
            currency={currency}
            currencyPicker={{ onChange: docCurrency.pick, disabled: currencyLocked }}
            warehouses={warehouses}
          />
      </CollapsibleHeaderCard>

      {/* ── The item table (SAL-01 §5c) — item-linked with free-text fallback */}
      <Card>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-11 text-center">#</TableHead>
                <TableHead className="min-w-[160px]">{t('quotes.colItem')}</TableHead>
                <TableHead className="min-w-[130px]">{t('orders.colWarehouse')}</TableHead>
                <TableHead className="w-24">{t('sales.colUnit')}</TableHead>
                <TableHead className="min-w-[200px]">{t('sales.colDescription')}
                  {editable ? <DescriptionLangToggle language={descLang} /> : null}
                </TableHead>
                <TableHead className="w-24 text-end">{t('sales.colQty')}</TableHead>
                {bonusOn ? <TableHead className="w-24 text-end">{t('sales.colBonus')}</TableHead> : null}
                {/* BR-SAL-01-05a: one price column per tier the company has
                    defined, in the company's own order — the map is the
                    layout, so no count is assumed anywhere. The applicable
                    column is the customer's own (BR-SAL-01-05), marked. */}
                {tierCols.length > 0 ? (
                  tierCols.map((col) => (
                    <TableHead
                      key={col.id}
                      className={cn(
                        'w-32 text-end',
                        col.id === applicableTier && 'underline decoration-dotted underline-offset-4',
                      )}
                      title={col.id === applicableTier ? t('quotes.tierApplies') : col.description ?? undefined}
                    >
                      {col.name}
                    </TableHead>
                  ))
                ) : (
                  <TableHead className="w-32 text-end">{t('sales.colUnitPrice')}</TableHead>
                )}
                {discountOn ? <TableHead className="w-28 text-end">{t('sales.colDisc')}</TableHead> : null}
                <TableHead className="w-32 text-end">{t('sales.colTotal')}</TableHead>
                {editable ? <TableHead className="w-11" /> : null}
              </TableRow>
            </TableHeader>
            <TableBody>
              {lines.map((line, index) => {
                const view = totals.lines[index];
                const activeWarehouses = (warehouses ?? []).filter((warehouse) => warehouse.isActive);
                return (
                  <TableRow key={line.key} className="odd:bg-muted/30">
                    <TableCell className="text-center font-mono text-muted-foreground">
                      {index + 1}
                    </TableCell>
                    <TableCell>
                      <ItemCell
                        label={t('quotes.cellItem', { line: index + 1 })}
                        clearLabel={t('quotes.freeTextLine')}
                        picker={itemPicker}
                        value={line.itemId}
                        onPick={(itemId) => pickItem(line.key, itemId)}
                        disabled={!editable}
                      />
                    </TableCell>
                    <TableCell>
                      {/* INV-L3 (QUO-01): the line's warehouse; empty = the header's. */}
                      {line.itemId ? (
                        <NativeSelect
                          aria-label={t('orders.cellWarehouse', { line: index + 1 })}
                          className="h-9 text-sm"
                          value={line.warehouseId}
                          onChange={(event) => setLine(line.key, { warehouseId: event.target.value })}
                          disabled={!editable}
                        >
                          <option value="">
                            {header.warehouseId
                              ? (activeWarehouses.find((w) => w.id === header.warehouseId)?.name ??
                                t('sales.pickWarehouse'))
                              : t('sales.pickWarehouse')}
                          </option>
                          {activeWarehouses.map((warehouse) => (
                            <option key={warehouse.id} value={warehouse.id}>
                              {warehouse.name}
                            </option>
                          ))}
                        </NativeSelect>
                      ) : (
                        <span className="px-2 text-sm text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {/* INV-L4: the item's sell units; empty = its stock unit. */}
                      <LineUnitSelect
                        item={line.itemId ? itemPicker.byId.get(line.itemId) : undefined}
                        value={line.unitId}
                        onChange={(unitId) => setLine(line.key, { unitId })}
                        disabled={!editable}
                        label={t('sales.cellUnit', { line: index + 1 })}
                      />
                    </TableCell>
                    <TableCell>
                      <CellInput
                        aria-label={t('sales.cellDescription', { line: index + 1 })}
                        value={line.description}
                        onChange={(event) =>
                          setLine(line.key, {
                            description: event.target.value,
                            typedDescription: true,
                          })
                        }
                        disabled={!editable}
                      />
                    </TableCell>
                    <TableCell>
                      <CellInput
                        mono
                        dir="ltr"
                        inputMode="decimal"
                        aria-label={t('sales.cellQty', { line: index + 1 })}
                        value={line.qty}
                        onChange={(event) => setLine(line.key, { qty: event.target.value })}
                        disabled={!editable}
                      />
                      <LineStockHint available={lineShort(line)} />
                    </TableCell>
                    {bonusOn ? (
                      <TableCell>
                        {/* INV-L1: free units given with the line. */}
                        <CellInput
                          mono
                          dir="ltr"
                          inputMode="decimal"
                          aria-label={t('sales.cellBonus', { line: index + 1 })}
                          value={line.bonusQty}
                          placeholder="0"
                          onChange={(event) => setLine(line.key, { bonusQty: event.target.value })}
                          disabled={!editable}
                        />
                      </TableCell>
                    ) : null}
                    {tierCols.length > 0 ? (
                      tierCols.map((col) =>
                        col.id === applicableTier ? (
                          // BR-SAL-01-05: the customer's own tier column IS
                          // the line price — editing it moves the total,
                          // which is ERR-SAL-01-08's repair (the prototype
                          // captured this figure and multiplied by the
                          // standard price anyway).
                          <TableCell key={col.id}>
                            <CellInput
                              mono
                              dir="ltr"
                              inputMode="decimal"
                              aria-label={t('sales.cellUnitPrice', { line: index + 1 })}
                              value={line.unitPrice}
                              onChange={(event) =>
                                setLine(line.key, {
                                  unitPrice: event.target.value,
                                  typedUnitPrice: true,
                                })
                              }
                              disabled={!editable}
                            />
                            <BelowCostHint cost={lineBelowCost(line)} />
                          </TableCell>
                        ) : (
                          // The other bands: the item's stored prices,
                          // shown for the salesperson's eye, priced by
                          // nothing on this line.
                          <TableCell key={col.id} className="text-end">
                            <span className="pe-2 font-mono text-sm text-muted-foreground">
                              {line.itemId && itemPicker.byId.get(line.itemId) ? (
                                <bdi dir="ltr">
                                  {tierPriceFor(itemPicker.byId.get(line.itemId)!, col.id)}
                                </bdi>
                              ) : (
                                '—'
                              )}
                            </span>
                          </TableCell>
                        ),
                      )
                    ) : (
                      <TableCell>
                        <CellInput
                          mono
                          dir="ltr"
                          inputMode="decimal"
                          aria-label={t('sales.cellUnitPrice', { line: index + 1 })}
                          value={line.unitPrice}
                          onChange={(event) =>
                            setLine(line.key, {
                              unitPrice: event.target.value,
                              typedUnitPrice: true,
                            })
                          }
                          disabled={!editable}
                        />
                        <BelowCostHint cost={lineBelowCost(line)} />
                      </TableCell>
                    )}
                    {discountOn ? (
                      <TableCell>
                        {/* INV-L2: drawn only while the company's switch is on. */}
                        <CellInput
                          mono
                          dir="ltr"
                          inputMode="decimal"
                          aria-label={t('sales.cellDisc', { line: index + 1 })}
                          value={line.discountPct}
                          placeholder="0"
                          onChange={(event) => setLine(line.key, { discountPct: event.target.value })}
                          disabled={!editable}
                        />
                        {view?.discountAmount && view.discountAmount !== '0.00' ? (
                          <div className="pe-2 text-end text-xs text-muted-foreground">
                            <bdi dir="ltr">−{view.discountAmount}</bdi>
                          </div>
                        ) : null}
                      </TableCell>
                    ) : null}
                    <TableCell className="text-end">
                      {view?.lineTotal !== null && view?.lineTotal !== undefined ? (
                        <Amount value={view.lineTotal} currency={currency} />
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    {editable ? (
                      <TableCell>
                        <button
                          type="button"
                          aria-label={t('sales.removeLine', { line: index + 1 })}
                          title={t('sales.removeLine', { line: index + 1 })}
                          onClick={() => removeLine(line.key)}
                          className="flex size-8 items-center justify-center rounded-md text-destructive transition-colors hover:bg-destructive/10"
                        >
                          <X className="size-4" aria-hidden />
                        </button>
                      </TableCell>
                    ) : null}
                  </TableRow>
                );
              })}
            </TableBody>
            <TableFooter>
              <TableRow>
                <TableCell colSpan={5}>
                  {editable ? (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="border-dashed"
                      onClick={() => setLines((current) => [...current, blankLine()])}
                    >
                      <Plus aria-hidden /> {t('sales.addLine')}
                    </Button>
                  ) : null}
                </TableCell>
                <TableCell className="text-end">
                  <span className="text-[10px] font-medium uppercase tracking-widest text-muted-foreground">
                    {t('sales.qtyTotal')}
                  </span>{' '}
                  <bdi dir="ltr" className="font-mono font-semibold">
                    {totals.qtyTotal}
                  </bdi>
                </TableCell>
                {/* priceColumnCount keeps this arithmetic honest whatever
                    number of tier columns the company defined (BR-SAL-01-05a:
                    no count-of-three in the layout). */}
                <TableCell
                  colSpan={priceColumnCount + 1 + (bonusOn ? 1 : 0) + (discountOn ? 1 : 0) + (editable ? 1 : 0)}
                  className="text-end"
                >
                  <span className="text-[10px] font-medium uppercase tracking-widest text-muted-foreground">
                    {t('sales.subtotal')}
                  </span>{' '}
                  <Amount value={totals.subtotal} currency={currency} className="font-semibold" />
                </TableCell>
              </TableRow>
            </TableFooter>
          </Table>
        </CardContent>
      </Card>

      {/* ── Workflow + terms | summary (SAL-01 §5d/§5e) ───────────────────── */}
      <div className="grid gap-4 lg:grid-cols-[1fr_380px]">
        <div className="grid gap-4">
          <Card>
            <CardHeader>
              <CardTitle>{t('quotes.workflow')}</CardTitle>
            </CardHeader>
            <CardContent className="grid gap-3">
              <div className="flex flex-wrap gap-2">
                {/* WHICH transitions exist is the flow's answer (`transitions`
                    is derived from the status); whether they are usable is the
                    role's, and that greys one button at a time so each carries
                    its own name and its own reason. */}
                {workflowTransitions.map((target) => {
                  const meta = transitionMeta[target];
                  const Icon = meta.icon;
                  return (
                    <Gated key={target} permission="sales.edit" reasonKey="perm.cannot.moveQuotes">
                      <Button
                        variant={target === 'rejected' ? 'destructive' : 'default'}
                        onClick={() => void onTransition(target)}
                        disabled={busy || isNew}
                      >
                        <Icon aria-hidden /> {t(meta.key)}
                      </Button>
                    </Gated>
                  );
                })}
                {/* W4/W5 — never hidden by expiry (R4). */}
                {!isNew && canConvert(status) ? (
                  <>
                    <Gated permission="sales.create" reasonKey="perm.cannot.convertQuotes">
                      <Button variant="outline" onClick={() => void onConvert('invoice')} disabled={busy}>
                        {t('quotes.convertInvoice')}
                      </Button>
                    </Gated>
                    <Gated permission="sales.create" reasonKey="perm.cannot.convertQuotes">
                      <Button variant="outline" onClick={() => void onConvert('order')} disabled={busy}>
                        {t('quotes.convertOrder')}
                      </Button>
                    </Gated>
                  </>
                ) : null}
                {/* BR-SAL-01-16: greyed on the permission, hidden for NOTHING else. */}
                {!isNew && canRevise(status) ? (
                  <Gated permission="sales.edit" reasonKey="perm.cannot.reviseQuotes">
                    <Button variant="outline" onClick={() => void onNewRevision()} disabled={busy}>
                      <FileClock aria-hidden /> {t('quotes.newRevision')}
                    </Button>
                  </Gated>
                ) : null}
              </div>
              <div className="grid gap-1.5">
                <Label htmlFor="qt-terms">{t('quotes.fTerms')}</Label>
                <Textarea
                  id="qt-terms"
                  placeholder={t('quotes.termsPh')}
                  maxLength={2000}
                  value={terms}
                  onChange={(event) => setTerms(event.target.value)}
                  disabled={!editable}
                />
              </div>
              <div className="grid gap-1.5">
                <Label htmlFor="qt-notes">{t('sales.notes')}</Label>
                <Textarea
                  id="qt-notes"
                  maxLength={2000}
                  value={notes}
                  onChange={(event) => setNotes(event.target.value)}
                  disabled={!editable}
                />
              </div>
            </CardContent>
          </Card>

          {/* SAL-01 §5's customer-acceptance card, behind its §5a switch.
              Accept quote stores the drawing with the quotation
              (BR-SAL-01-13); an empty pad answers with ERR-SAL-01-07's
              sentence beside the pad. */}
          {signatureOn && !isNew ? (
            <QuoteAcceptanceCard
              stored={quotation?.signature ?? null}
              canAccept={transitions.includes('accepted')}
              busy={busy}
              onAccept={(signature) => void onTransition('accepted', signature)}
            />
          ) : null}

          {/* §9 — the revisions table: prior versions, readable forever. */}
          {!isNew && (quotation?.revisions.length ?? 0) > 0 ? (
            <Card>
              <CardHeader>
                <CardTitle>{t('quotes.revisions')}</CardTitle>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-16">{t('quotes.colRev')}</TableHead>
                      <TableHead>{t('common.date')}</TableHead>
                      <TableHead className="text-end">{t('sales.colGrandTotal')}</TableHead>
                      <TableHead>{t('quotes.colNote')}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {/* The current row's label follows the STATUS (wave 1B):
                        only a draft is "being edited"; a sent, accepted,
                        rejected, expired or converted head is drawn as what
                        it is — the same words the status chip uses. */}
                    <TableRow className="bg-violet-50/50 dark:bg-violet-950/30">
                      <TableCell className="font-mono font-semibold">
                        <bdi dir="ltr">{`v${revisionNo}`}</bdi>
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {status === 'draft'
                          ? t('quotes.currentRevision')
                          : t('quotes.currentRevisionAs', { status: t(`quotes.status.${status}`) })}
                      </TableCell>
                      <TableCell className="text-end">
                        {totals.grandTotal !== null ? (
                          <Amount value={totals.grandTotal} currency={currency} />
                        ) : (
                          '—'
                        )}
                      </TableCell>
                      <TableCell />
                    </TableRow>
                    {quotation!.revisions.map((revision) => (
                      <TableRow key={revision.revisionNo}>
                        <TableCell className="font-mono text-muted-foreground">
                          <bdi dir="ltr">{`v${revision.revisionNo}`}</bdi>
                        </TableCell>
                        <TableCell className="font-mono text-muted-foreground">
                          <Ltr value={revision.createdAt.slice(0, 10)} />
                          {/* The status the prior version was FROZEN in — a
                              revision opened from an accepted offer says so. */}
                          <span className="ms-2 font-sans text-xs">
                            {t(`quotes.status.${revision.snapshot.status}`)}
                          </span>
                        </TableCell>
                        <TableCell className="text-end">
                          <Amount value={revision.snapshot.grandTotal} currency={currency} />
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground">
                          {t('quotes.frozenNote')}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          ) : null}
        </div>

        {/* SAL-01 §5e — the payment-less summary: no due date, nothing owed. */}
        <Card className="self-start">
          <CardContent className="grid gap-3">
            {/* Wave 4C — the shared totals rows (F-2…F-7), with the currency code by the total. */}
            <DocumentTotals
              currency={currency}
              subtotal={totals.subtotal}
              discountAmount={totals.discountAmount}
              discountControl={
                <TwoWayPair
                  kind="discount"
                  state={discount}
                  onChange={setDiscount}
                  pct={totals.discountPct ?? '0.00'}
                  amount={totals.discountAmount ?? ''}
                  disabled={!editable}
                />
              }
              taxAmount={totals.taxAmount}
              vatControl={
                <TwoWayPair
                  kind="vat"
                  state={tax}
                  onChange={setTax}
                  pct={totals.taxPct ?? defaultTaxPct}
                  amount={totals.taxAmount ?? ''}
                  disabled={!editable}
                />
              }
              grandTotal={totals.grandTotal}
            />
            {/* EC-19-10 on the offer itself: a quotation freezes its rate when
                it is SENT, and a customer holding it is entitled to see which
                rate the figure they were quoted stands on. Hidden entirely
                for a base-currency quotation (GEN-116). */}
            {/* The FxPanel now sits in the header's Currency & rate cell (INV-F5). */}
            {/* §5e's note made visible: nothing is owed on a quotation. */}
            {/* Wave 4B — the offer's margin, for a reader allowed to see cost. */}
            <MarginPanel
              lines={lines}
              items={itemPicker.byId}
              net={netAmount(totals.subtotal, totals.discountAmount, currency)}
              currency={currency}
            />
            <p className="text-xs text-muted-foreground">{t('quotes.noPaymentNote')}</p>
          </CardContent>
        </Card>
      </div>
      {/* Wave 2 — INV-H5's files panel on this document. Hidden
          behind "save first" on a new draft; permission follows the record. */}
      <AttachmentsPanel kind="quotation" entityId={quotationId} />
    </div>
  );
}
